import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { events } from 'services/analytics';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import { useSubscription } from 'services/hooks';
import useWavesurferContext from 'services/hooks/wavesurfer';
import useAnalytics from 'services/hooks/analytics';
import { selectIsSubmitReleaseLimitReached } from 'services/store/user/selectors';
import { UpsellModal } from 'components';
import NewReleaseModal from 'components/Modals/implementations/NewReleaseModal';
import UpgradeDistributionModal from 'components/Modals/UpgradeModal/UpgradeDistributionModal';
import { UploadAudioModal } from 'components/Modals/UploadAudioModal';
import { ReleaseLimitDialog } from 'components/Modals/implementations/ReleaseLimitDialog';
import {
  ReleasesContainer,
  ReleasesNavbarContainer,
  OutletContainer,
} from './styled';
import { NewReleaseButton, ReleasesNavbar } from './components';

const ReleaseTabView = () => {
  const { song: isMiniPlayerVisible } = useWavesurferContext();

  const { isFreeAccount, hasDistribution } = useSubscription();
  const { track } = useAnalytics();

  const [isNewReleaseModalOpen, setIsNewReleaseModalOpen] = useState(false);
  const [isUploadAudioModalOpen, setIsUploadAudioModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const [isReleaseLimitDialogOpen, setIsReleaseLimitDialogOpen] =
    useState(false);

  const isReleaseLimitReached = useSelector(selectIsSubmitReleaseLimitReached);

  const handleNewReleaseClick = useCallback(() => {
    track(events.newReleaseButtonClicked());

    if (isReleaseLimitReached) {
      setIsReleaseLimitDialogOpen(true);
      return;
    }

    setIsNewReleaseModalOpen(true);
  }, [track, isReleaseLimitReached]);

  const handleOpenUploadAudioModal = useCallback(() => {
    if (hasDistribution) {
      setIsUploadAudioModalOpen(true);
      return;
    }

    if (isFreeAccount) {
      setIsUpgradeModalOpen(true);
      return;
    }

    setIsUpsellModalOpen(true);
  }, [hasDistribution, isFreeAccount]);

  const handleNewReleaseModalClose = () => setIsNewReleaseModalOpen(false);
  const handleUploadAudioModalClose = () => setIsUploadAudioModalOpen(false);
  const handleUpgradeModalClose = () => setIsUpgradeModalOpen(false);
  const handleUpsellModalClose = () => setIsUpsellModalOpen(false);
  const handleReleaseLimitDialogClose = () =>
    setIsReleaseLimitDialogOpen(false);

  return (
    <ReleasesContainer>
      <ReleasesNavbarContainer>
        <NewReleaseButton onClick={handleNewReleaseClick} />
        <ReleasesNavbar />
      </ReleasesNavbarContainer>
      <OutletContainer isMiniPlayerVisible={!!isMiniPlayerVisible}>
        <Outlet />
      </OutletContainer>

      <NewReleaseModal
        isOpen={isNewReleaseModalOpen}
        onClose={handleNewReleaseModalClose}
        onOpenUploadAudioModal={handleOpenUploadAudioModal}
      />
      <UploadAudioModal
        isOpen={isUploadAudioModalOpen}
        onClose={handleUploadAudioModalClose}
      />
      <UpsellModal
        isOpen={isUpsellModalOpen}
        onClose={handleUpsellModalClose}
        location={AnalyticsEventLocation.NEW_RELEASE_MODAL}
      />
      <UpgradeDistributionModal
        isOpen={isUpgradeModalOpen}
        onClose={handleUpgradeModalClose}
        location={AnalyticsEventLocation.NEW_RELEASE_MODAL}
      />
      <ReleaseLimitDialog
        isOpen={isReleaseLimitDialogOpen}
        onClose={handleReleaseLimitDialogClose}
      />
    </ReleasesContainer>
  );
};

export default ReleaseTabView;
